import { useMutation, useQueryClient } from "@tanstack/react-query"
import { updateBrandColors, createBrandFont, destroyBrandFont } from "sdk/brand"
import { brandKeys, useBrandColors } from "sdk/brand/queries"

const HEX_COLOR = /^#[0-9a-f]{6}$/i

/**
 * Saves the account's saved colors, and writes through to the legacy
 * `window.app.user.colors` global so not-yet-migrated pickers stay fresh.
 */
export function useUpdateBrandColors(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: updateBrandColors,
    async onSuccess(colors, ...rest) {
      if (window.app?.user) window.app.user.colors = colors
      queryClient.invalidateQueries({ queryKey: brandKeys.colors() })
      await options.onSuccess?.(colors, ...rest)
    }
  })
}

function updateWebFonts(queryClient, update) {
  const queryKey = brandKeys.fonts("web")
  const previous = queryClient.getQueryData(queryKey)
  queryClient.setQueryData(queryKey, (fonts = []) => update(fonts))
  return { queryKey, previous }
}

export function useCreateBrandFont(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: createBrandFont,
    onMutate({ label, emailSafe, fileUrl }) {
      return updateWebFonts(queryClient, fonts => [
        ...fonts,
        {
          id: `pending-${label}`,
          label,
          email_safe: emailSafe,
          file_url: fileUrl
        }
      ])
    },
    async onSuccess(font, ...rest) {
      queryClient.invalidateQueries({ queryKey: [...brandKeys.all, "fonts"] })
      await options.onSuccess?.(font, ...rest)
    },
    onError(error, variables, { queryKey, previous }) {
      queryClient.setQueryData(queryKey, previous)
      return options.onError?.(error, variables, { queryKey, previous })
    }
  })
}

export function useDestroyBrandFont(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: destroyBrandFont,
    onMutate({ id }) {
      return updateWebFonts(queryClient, fonts =>
        fonts.filter(font => font.id !== id)
      )
    },
    async onSuccess(...args) {
      queryClient.invalidateQueries({ queryKey: [...brandKeys.all, "fonts"] })
      await options.onSuccess?.(...args)
    },
    onError(error, variables, { queryKey, previous }) {
      queryClient.setQueryData(queryKey, previous)
      return options.onError?.(error, variables, { queryKey, previous })
    }
  })
}

export function useAddBrandColor() {
  const { data: colors = [] } = useBrandColors()
  const { mutate } = useUpdateBrandColors()

  return color => {
    // Guard the two cases the "+" can still be clicked in but the server would
    // reject: a non-hex value (e.g. "transparent") and a color already saved.
    if (!HEX_COLOR.test(color)) return
    const normalized = color.toUpperCase()
    if (colors.includes(normalized)) return
    mutate([...colors, normalized])
  }
}
